import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Search, X, Shield, Users } from 'lucide-react';
import apiClient from '../services/api';
import '../styles/MasterForms.css';

const UserTypes = () => {
  const [userTypes, setUserTypes] = useState([]);
  const [filteredUserTypes, setFilteredUserTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({
    usertypeid: null,
    usertypename: '',
    description: '',
    isactive: true
  });
  const [errors, setErrors] = useState({});
  const [submitLoading, setSubmitLoading] = useState(false);

  useEffect(() => {
    fetchUserTypes();
  }, []);

  useEffect(() => {
    filterUserTypes();
  }, [searchTerm, userTypes]);

  const fetchUserTypes = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get('/UserTypes.php');
      
      if (response.data.success) {
        setUserTypes(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching user types:', error);
      if (error.response?.status === 401) {
        alert('Session expired. Please login again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const filterUserTypes = () => {
    if (!searchTerm.trim()) {
      setFilteredUserTypes(userTypes);
    } else {
      const filtered = userTypes.filter(type =>
        type.usertypename.toLowerCase().includes(searchTerm.toLowerCase()) ||
        type.description?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredUserTypes(filtered);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.usertypename.trim()) {
      newErrors.usertypename = 'User type name is required';
    } else if (formData.usertypename.length > 50) {
      newErrors.usertypename = 'User type name must be 50 characters or less';
    }
    
    if (formData.description && formData.description.length > 255) {
      newErrors.description = 'Description must be 255 characters or less';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setSubmitLoading(true);
    try {
      const method = editMode ? 'put' : 'post';
      const response = await apiClient[method]('/UserTypes.php', {
        ...formData,
        isactive: formData.isactive ? 1 : 0
      });
      
      const data = response.data;
      
      if (data.success) {
        alert(editMode ? 'User type updated successfully!' : 'User type created successfully!');
        setShowModal(false);
        resetForm();
        fetchUserTypes();
      } else {
        alert('Error: ' + data.message);
      }
    } catch (error) {
      console.error('Error saving user type:', error);
      if (error.response?.status === 401) {
        alert('Session expired. Please login again.');
      } else if (error.response?.status === 409) {
        alert('User type name already exists. Please use a different name.');
      } else {
        alert('Error saving user type. Please try again.');
      }
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleEdit = (userType) => {
    setFormData({
      usertypeid: userType.usertypeid,
      usertypename: userType.usertypename,
      description: userType.description || '',
      isactive: userType.isactive === 1
    });
    setEditMode(true);
    setShowModal(true);
    setErrors({});
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this user type? This will affect users assigned to this type.')) {
      return;
    }
    
    try {
      const response = await apiClient.delete('/UserTypes.php', {
        data: { usertypeid: id }
      });
      
      const data = response.data;
      
      if (data.success) {
        alert('User type deleted successfully!');
        fetchUserTypes();
      } else {
        alert('Error: ' + data.message);
      }
    } catch (error) {
      console.error('Error deleting user type:', error);
      if (error.response?.status === 401) {
        alert('Session expired. Please login again.');
      } else if (error.response?.status === 409) {
        alert('Cannot delete user type with associated users.');
      } else {
        alert('Error deleting user type. Please try again.');
      }
    }
  };

  const resetForm = () => {
    setFormData({
      usertypeid: null,
      usertypename: '',
      description: '',
      isactive: true
    });
    setEditMode(false);
    setErrors({});
  };

  const openAddModal = () => {
    resetForm();
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    resetForm();
  };

  return (
    <div className="master-container">
      <div className="master-header">
        <div className="header-left">
          <h2>
            <Shield size={32} style={{ display: 'inline', marginRight: '10px', verticalAlign: 'middle' }} />
            User Types Master
          </h2>
          <p className="subtitle">Manage user role types and permissions groups</p>
        </div>
        <button className="btn-primary" onClick={openAddModal}>
          <Plus size={20} />
          Add User Type
        </button>
      </div>

      <div className="master-toolbar">
        <div className="search-box">
          <Search size={20} />
          <input
            type="text"
            placeholder="Search by name or description..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <button className="btn-clear" onClick={() => setSearchTerm('')}>
              <X size={16} />
            </button>
          )}
        </div>
        <div className="toolbar-info">
          Total: {filteredUserTypes.length} {filteredUserTypes.length === 1 ? 'type' : 'types'}
        </div>
      </div>

      {loading ? (
        <div className="loading-state">Loading user types...</div>
      ) : filteredUserTypes.length === 0 ? (
        <div className="empty-state">
          <p>No user types found</p>
          {searchTerm && <button onClick={() => setSearchTerm('')}>Clear search</button>}
        </div>
      ) : (
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>User Type Name</th>
                <th>Description</th>
                <th>Status</th>
                <th>Created At</th>
                <th>Updated At</th>
                <th className="actions-column">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredUserTypes.map((type) => (
                <tr key={type.usertypeid}>
                  <td className="font-semibold">{type.usertypename}</td>
                  <td>{type.description || '-'}</td>
                  <td>
                    <span className={`status-badge ${type.isactive ? 'active' : 'inactive'}`}>
                      {type.isactive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>{new Date(type.createdat).toLocaleDateString()}</td>
                  <td>{new Date(type.updatedat).toLocaleDateString()}</td>
                  <td className="actions-column">
                    <button
                      className="btn-icon btn-edit"
                      onClick={() => handleEdit(type)}
                      title="Edit"
                    >
                      <Edit2 size={16} />
                    </button>
                    <button
                      className="btn-icon btn-delete"
                      onClick={() => handleDelete(type.usertypeid)}
                      title="Delete"
                    >
                      <Trash2 size={16} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editMode ? 'Edit User Type' : 'Add New User Type'}</h3>
              <button className="btn-close" onClick={closeModal}>
                <X size={24} />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="modal-form">
              <div className="form-row">
                <div className="form-group" style={{ flex: 1 }}>
                  <label htmlFor="usertypename">
                    User Type Name <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    id="usertypename"
                    value={formData.usertypename}
                    onChange={(e) => setFormData({ ...formData, usertypename: e.target.value })}
                    maxLength="50"
                    placeholder="e.g., Administrator, Manager, User"
                    className={errors.usertypename ? 'error' : ''}
                  />
                  {errors.usertypename && (
                    <span className="error-message">{errors.usertypename}</span>
                  )}
                </div>
              </div>

              <div className="form-row">
                <div className="form-group" style={{ flex: 1 }}>
                  <label htmlFor="description">Description</label>
                  <textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    maxLength="255"
                    placeholder="Describe the role and responsibilities..."
                    rows="4"
                    className={errors.description ? 'error' : ''}
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '2px solid #e5e7eb',
                      borderRadius: '8px',
                      fontSize: '0.95rem',
                      fontFamily: 'inherit',
                      resize: 'vertical'
                    }}
                  />
                  {errors.description && (
                    <span className="error-message">{errors.description}</span>
                  )}
                  <small style={{ color: '#6B7280', marginTop: '4px', display: 'block' }}>
                    Maximum 255 characters
                  </small>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={formData.isactive}
                      onChange={(e) => setFormData({ ...formData, isactive: e.target.checked })}
                    />
                    <span>Active</span>
                  </label>
                </div>
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={closeModal}
                  disabled={submitLoading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={submitLoading}
                >
                  {submitLoading ? 'Saving...' : (editMode ? 'Update' : 'Create')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserTypes;